'use client';
import React, { useState } from 'react';
import { useModel } from '@/context/ModelContext';
import { fmtCrShort } from '@/lib/financial-model';
import { Info } from 'lucide-react';

type DisplayMode = 'crores' | 'absolute';

function fmtVal(val: number, mode: DisplayMode): string {
  if (val === 0) return '—';
  if (mode === 'crores') return fmtCrShort(val);
  return `₹${Math.round(Math.abs(val)).toLocaleString('en-IN')}${val < 0 ? ' (Dr)' : ''}`;
}

export default function CashFlowTable() {
  const { model } = useModel();
  const [displayMode, setDisplayMode] = useState<DisplayMode>('crores');
  const years = model.years;

  function v(val: number) {
    return fmtVal(val, displayMode);
  }

  // Cumulative project FCF
  let cumPFcf = 0;
  const cumulativePFcf = years.map((y) => {
    cumPFcf += y.projectFcf;
    return cumPFcf;
  });

  interface CfRow {
    id: string;
    label: string;
    isSection?: boolean;
    isHighlight?: boolean;
    values: number[];
    isNegative?: boolean;
    indent?: boolean;
  }

  const projectRows: CfRow[] = [
    { id: 'ebitda-cf', label: 'EBITDA', values: years.map((y) => y.ebitda), isHighlight: false },
    {
      id: 'tax-cf',
      label: 'Less: Tax on EBIT (approx)',
      values: years.map((y) => -y.tax),
      isNegative: true,
      indent: true,
    },
    {
      id: 'capex-cf',
      label: 'Less: Capex / WC Investment',
      values: years.map((y) => (y.year === 0 ? -model.totalProjectCost : 0)),
      isNegative: true,
      indent: true,
    },
    {
      id: 'pfcf',
      label: 'PROJECT FREE CASH FLOW',
      values: years.map((y) => y.projectFcf),
      isHighlight: true,
    },
    {
      id: 'cumulative',
      label: 'Cumulative Project FCF',
      values: cumulativePFcf,
      isHighlight: false,
    },
  ];

  const equityRows: CfRow[] = [
    { id: 'pfcf-eq', label: 'Project FCF', values: years.map((y) => y.projectFcf) },
    {
      id: 'debt-movement',
      label: 'Debt Drawdown / (Repayment)',
      values: years.map((y) => y.debtMovement),
      indent: true,
    },
    {
      id: 'interest-at',
      label: 'Less: Interest (After Tax)',
      values: years.map((y) => -y.interestAfterTax),
      isNegative: true,
      indent: true,
    },
    {
      id: 'efcf',
      label: 'EQUITY FREE CASH FLOW',
      values: years.map((y) => y.equityFcf),
      isHighlight: true,
    },
  ];

  const dscrRows: CfRow[] = [
    { id: 'ebitda-ds', label: 'EBITDA', values: years.map((y) => y.ebitda) },
    { id: 'interest-ds', label: 'Interest', values: years.map((y) => y.interest), indent: true },
    {
      id: 'principal-ds',
      label: 'Principal Repayment',
      values: years.map((y) => y.principalRepayment),
      indent: true,
    },
    {
      id: 'dscr-row',
      label: 'DSCR (EBITDA / Debt Service)',
      values: years.map((y) => y.dscr),
      isHighlight: true,
    },
    {
      id: 'outstanding',
      label: 'Outstanding Debt Balance',
      values: years.map((y) => y.outstandingDebt),
    },
  ];

  const colHeaders = [
    'Particulars',
    'Yr 0',
    ...Array.from({ length: 10 }, (_, i) => `Year ${i + 1}`),
  ];

  function renderSection(title: string, rows: CfRow[]) {
    return (
      <div className="bg-card border border-border rounded-xl overflow-hidden mb-4">
        <div className="px-4 py-3 bg-muted border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        </div>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-xs border-collapse" style={{ minWidth: '860px' }}>
            <thead>
              <tr className="bg-muted/50 border-b border-border">
                {colHeaders.map((h, idx) => (
                  <th
                    key={`cf-header-${title}-${idx}`}
                    className={`py-2.5 px-3 font-semibold text-muted-foreground text-xs tracking-wide
                      ${idx === 0 ? 'text-left sticky-col min-w-[220px]' : 'text-right min-w-[80px] whitespace-nowrap'}
                    `}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={`cf-row-${row.id}`}
                  className={`border-b border-border/50 hover:bg-muted/30 transition-colors
                    ${row.isHighlight ? 'highlight-row' : ''}
                  `}
                >
                  <td
                    className={`py-2.5 px-3 sticky-col
                    ${row.isHighlight ? 'font-semibold text-foreground' : row.indent ? 'pl-6 text-muted-foreground' : 'text-foreground'}
                  `}
                  >
                    {row.label}
                  </td>
                  {years.map((y, yIdx) => {
                    const val = row.values[yIdx];
                    let displayStr: string;

                    // DSCR row special formatting
                    if (row.id === 'dscr-row') {
                      displayStr = val === 0 ? '—' : `${val.toFixed(2)}x`;
                    } else {
                      displayStr = v(val);
                    }

                    const isNeg = val < 0;
                    const isDscrLow = row.id === 'dscr-row' && val > 0 && val < 1.5;
                    const isDscrGood = row.id === 'dscr-row' && val >= 1.5;

                    return (
                      <td
                        key={`cf-cell-${row.id}-yr${yIdx}`}
                        className={`py-2.5 px-3 text-right table-cell-mono
                          ${isNeg ? 'negative-value' : ''}
                          ${row.isHighlight ? 'font-semibold' : ''}
                          ${isDscrLow ? 'text-danger font-semibold' : ''}
                          ${isDscrGood ? 'text-primary font-semibold' : ''}
                          ${y.year === 0 ? 'text-muted-foreground bg-muted/20' : ''}
                        `}
                      >
                        {displayStr}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">
            Cash Flow, Debt Service & Returns — 5 TPH — 10 Year
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Project FCF (unlevered) · Equity FCF (levered) · DSCR schedule
          </p>
        </div>
        <div className="flex gap-2">
          {(['crores', 'absolute'] as DisplayMode[]).map((mode) => (
            <button
              key={`cf-display-${mode}`}
              onClick={() => setDisplayMode(mode)}
              className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-all duration-150
                ${
                  displayMode === mode
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card text-muted-foreground border-border hover:bg-muted'
                }`}
            >
              {mode === 'crores' ? '₹ Crores' : '₹ Absolute'}
            </button>
          ))}
        </div>
      </div>

      {renderSection('Project Cash Flow (Unlevered)', projectRows)}
      {renderSection('Equity Cash Flow (Levered)', equityRows)}
      {renderSection('Debt Service Coverage Ratio (DSCR)', dscrRows)}

      <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
        <Info size={13} className="text-muted-foreground mt-0.5 flex-shrink-0" />
        <p className="text-xs text-muted-foreground">
          Interest After Tax = Interest × (1 − {(model.assumptions.taxRate * 100).toFixed(0)}%) ·
          Principal repayment of {fmtCrShort(model.principalRepaymentPerYear)}/year from Year 2 to
          Year {model.assumptions.loanTenure} · DSCR = EBITDA ÷ (Interest + Principal); target ≥
          1.50x per CGTMSE covenant · No terminal value in FCF stream
        </p>
      </div>
    </div>
  );
}
